import m from 'mithril'

import { initMithrilComponent } from '@client-shared/utils/mithril'
import type { IPhaseMappingData } from '@app/queries/optimus/optimus-config'
import UserModel from '@app/models/users'

import type { OverlapView } from './phase-mapping'
import { ADMIN_UNNAMED_VIEW, MOVEMENT_TYPE_ENUM } from './phase-mapping'
import css from './phase-mapping.module.css'
import UnstyledButton from '@client-shared/components-v2/unstyled-button'

export interface IOverlapViewSwitcherAttrs {
	phase_mapping: IPhaseMappingData | undefined
	selected_overlap_view: OverlapView
	dropdown_open: boolean
	new_overlap_name_input: string | null
	renaming_overlap_name: string | null
	renaming_overlap_value: string
	onSelectView: (view: OverlapView) => void
	onToggleDropdown: () => void
	onCloseDropdown: () => void
	onStartRename: (name: string) => void
	onRenameValueChange: (value: string) => void
	onCommitRename: () => void
	onCancelRename: () => void
	onDeleteOverlap: (name: string) => void
	onNewOverlapInputChange: (value: string) => void
	onCommitNewOverlap: () => void
	onCancelNewOverlap: () => void
	onStartNewOverlap: () => void
}

function collectOverlapNames(phase_mapping?: IPhaseMappingData): string[] {
	const seen: string[] = []
	for (const p of phase_mapping?.phase_mapping ?? []) {
		for (const am of p.atomic_movements ?? []) {
			if (am.overlap_name && !seen.includes(am.overlap_name)) seen.push(am.overlap_name)
		}
	}
	return seen.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
}

function countUnnamedOverlapRows(phase_mapping?: IPhaseMappingData): number {
	let n = 0
	for (const p of phase_mapping?.phase_mapping ?? []) {
		for (const am of p.atomic_movements ?? []) {
			if (am.movement_type === MOVEMENT_TYPE_ENUM.overlap && !am.overlap_name) n += 1
		}
	}
	return n
}

export const OverlapViewSwitcher = initMithrilComponent<IOverlapViewSwitcherAttrs>()

OverlapViewSwitcher.view = (vnode) => {
	const {
		phase_mapping,
		selected_overlap_view,
		dropdown_open,
		new_overlap_name_input,
		renaming_overlap_name,
		renaming_overlap_value,
		onSelectView,
		onToggleDropdown,
		onCloseDropdown,
		onStartRename,
		onRenameValueChange,
		onCommitRename,
		onCancelRename,
		onDeleteOverlap,
		onNewOverlapInputChange,
		onCommitNewOverlap,
		onCancelNewOverlap,
		onStartNewOverlap,
	} = vnode.attrs

	const overlap_names = collectOverlapNames(phase_mapping)
	const unnamed_count = countUnnamedOverlapRows(phase_mapping)
	const trigger_label =
		selected_overlap_view === null
			? 'Default'
			: selected_overlap_view === ADMIN_UNNAMED_VIEW
				? `Unnamed (${unnamed_count})`
				: `Overlap ${selected_overlap_view}`

	return (
		<div className={css.overlap_view_bar}>
			<div className={css.overlap_view_dropdown}>
				<div className={css.overlap_view_trigger} onclick={onToggleDropdown}>
					<div>
						<span className={css.overlap_view_label}>View</span>
						<span>{trigger_label}</span>
					</div>
					<span className={css.chevron}>▾</span>
				</div>
				{dropdown_open && (
					<>
						<div className={css.overlap_view_backdrop} onclick={onCloseDropdown} />
						<div className={css.overlap_view_list}>
							<div
								className={[
									css.overlap_view_option,
									selected_overlap_view === null ? css.overlap_view_option_active : '',
								]
									.filter(Boolean)
									.join(' ')}
								onclick={() => onSelectView(null)}
							>
								Default
							</div>
							{overlap_names.map((name) =>
								renaming_overlap_name === name ? (
									<div key={`overlap-name-input-${name}`} className={css.overlap_view_option}>
										<input
											className={css.overlap_view_inline_input}
											value={renaming_overlap_value}
											oncreate={(el: m.VnodeDOM) => (el.dom as HTMLInputElement).focus()}
											oninput={(e: InputEvent) => onRenameValueChange((e.target as HTMLInputElement).value)}
											onkeydown={(e: KeyboardEvent) => {
												if (e.key === 'Enter') {
													e.preventDefault()
													onCommitRename()
												} else if (e.key === 'Escape') {
													onCancelRename()
												}
											}}
											onblur={onCommitRename}
										/>
									</div>
								) : (
									<div
										key={`overlap-name-button-${name}`}
										className={[
											css.overlap_view_option,
											selected_overlap_view === name ? css.overlap_view_option_active : '',
										]
											.filter(Boolean)
											.join(' ')}
										onclick={() => onSelectView(name)}
									>
										<span>{`Overlap ${name}`}</span>
										<div className={css.overlap_view_actions}>
											<UnstyledButton
												className={css.overlap_view_rename_btn}
												title="Rename"
												onclick={(e: MouseEvent) => {
													e.stopPropagation()
													onStartRename(name)
												}}
											>
												✎
											</UnstyledButton>
											<UnstyledButton
												className={css.overlap_view_delete_btn}
												title="Delete overlap"
												onclick={(e: MouseEvent) => {
													e.stopPropagation()
													onDeleteOverlap(name)
												}}
											>
												🗑
											</UnstyledButton>
										</div>
									</div>
								),
							)}
							{new_overlap_name_input !== null ? (
								<div className={css.overlap_view_add_row}>
									<input
										className={css.overlap_view_inline_input}
										placeholder="Overlap name"
										value={new_overlap_name_input}
										oncreate={(el: m.VnodeDOM) => (el.dom as HTMLInputElement).focus()}
										oninput={(e: InputEvent) => onNewOverlapInputChange((e.target as HTMLInputElement).value)}
										onkeydown={(e: KeyboardEvent) => {
											if (e.key === 'Enter') {
												e.preventDefault()
												onCommitNewOverlap()
											} else if (e.key === 'Escape') {
												onCancelNewOverlap()
											}
										}}
										onblur={onCommitNewOverlap}
									/>
								</div>
							) : (
								<div className={css.overlap_view_add_row} onclick={onStartNewOverlap}>
									+ Add Overlap
								</div>
							)}
							{UserModel.is_super_admin && unnamed_count > 0 && (
								<div
									className={[
										css.overlap_view_option,
										css.overlap_view_admin_option,
										selected_overlap_view === ADMIN_UNNAMED_VIEW ? css.overlap_view_option_active : '',
									]
										.filter(Boolean)
										.join(' ')}
									onclick={() => onSelectView(ADMIN_UNNAMED_VIEW)}
								>
									<span>{`Unnamed overlaps (${unnamed_count})`}</span>
									<span className={css.overlap_view_admin_badge}>ADMIN</span>
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</div>
	)
}
